import { Grid, Typography } from '@mui/material';
import { range } from 'lodash';
import type { FieldError, Path } from 'react-hook-form';
import { SidebarAccordion } from 'src/components/accordion';
import {
  ColorPickerWidget,
  InputWidget,
  NumberInputWidget,
  SwitchButtonWidget,
} from 'src/components/widget';
import {
  QuillTheme,
  RichTextEditor,
  RichTextToolbarOptions,
} from 'src/components/widget/richTextWidget';
import { FONTS_OPTIONS } from 'src/constants';
import { useGetProjectConfig } from 'src/hooks';
import { lang } from 'src/utils';

import {
  hasEmptyMessage,
  sanitizeRichTextInObject,
} from '../../../EditComponentPage.helper';
import { InfoBox } from '../../../modules/InfoBox';
import type { CodConfigInterface } from '../CODForm.interface';
import { TitleAccordionProps } from './TitleAccordion.interface';

export const TitleAccordion = (props: TitleAccordionProps) => {
  const hiddenToolbarItems: RichTextToolbarOptions[] = [
    RichTextToolbarOptions.STRIKE,
  ];
  const { data: projectConfig } = useGetProjectConfig();

  const disabled = false;
  const projectFonts = projectConfig?.fonts?.map((item) => item.name) ?? [];
  const mappedFonts = projectFonts.length > 0 ? projectFonts : FONTS_OPTIONS;

  const options = {
    placeholder: 'Enter text...',
    richTextTheme: QuillTheme.TOOLBAR,
  };

  const handleFieldChange =
    (field: Path<CodConfigInterface>) => (value: any) => {
      props.handleOnChange(`codSettings.codSetting.${field}`, value);
    };

  const titleItems = [
    {
      label: 'Your order',
      hasError: false,
      content: (
        <Grid container spacing={2} padding={3}>
          <Grid item xs={12}>
            <InputWidget
              label="Your order label"
              value={props.codSetting?.header?.order?.label}
              onChange={handleFieldChange('header.order.label')}
              hasError={!!props.errors?.header?.order?.label}
              error={props.errors?.header?.order?.label?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Font size"
              value={props.codSetting?.header?.order?.fontSize}
              onChange={handleFieldChange('header.order.fontSize')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.header?.order?.fontSize}
              error={props.errors?.header?.order?.fontSize?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Line height"
              value={props.codSetting?.header?.order?.lineHeight}
              onChange={handleFieldChange('header.order.lineHeight')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.header?.order?.lineHeight}
              error={props.errors?.header?.order?.lineHeight?.message}
            />
          </Grid>
          <Grid item xs={2} container alignItems="flex-end">
            <ColorPickerWidget
              value={props.codSetting?.header?.order?.color}
              onChange={handleFieldChange('header.order.color')}
              hasError={!!props.errors?.header?.order?.color}
              error={props.errors?.header?.order?.color?.message}
            />
          </Grid>
          <Grid item xs={12}>
            <SwitchButtonWidget
              label="Pagination"
              value={props.codSetting?.header?.pagination?.enable}
              onChange={handleFieldChange('header.pagination.enable')}
              hasError={!!props.errors?.header?.pagination?.enable}
              error={props.errors?.header?.pagination?.enable?.message}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Total label',
      hasError: false,
      content: (
        <Grid container spacing={2} padding={3}>
          <Grid item xs={12}>
            <InputWidget
              label="Total label"
              value={props.codSetting?.total?.total?.label}
              onChange={handleFieldChange('total.total.label')}
              hasError={!!props.errors?.total?.total?.label}
              error={props.errors?.total?.total?.label?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Font size"
              value={props.codSetting?.total?.total?.fontSize}
              onChange={handleFieldChange('total.total.fontSize')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.total?.fontSize}
              error={props.errors?.total?.total?.fontSize?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Line height"
              value={props.codSetting?.total?.total?.lineHeight}
              onChange={handleFieldChange('total.total.lineHeight')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.total?.lineHeight}
              error={props.errors?.total?.total?.lineHeight?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Letter spacing"
              value={props.codSetting?.total?.total?.letterSpacing}
              onChange={handleFieldChange('total.total.letterSpacing')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.total?.letterSpacing}
              error={props.errors?.total?.total?.letterSpacing?.message}
            />
          </Grid>
          <Grid item xs={2} container alignItems="flex-end">
            <ColorPickerWidget
              value={props.codSetting?.total?.total?.color}
              onChange={handleFieldChange('total.total.color')}
              hasError={!!props.errors?.total?.total?.color}
              error={props.errors?.total?.total?.color?.message}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Savings label',
      hasError: false,
      content: (
        <Grid container spacing={2} padding={3}>
          <Grid item xs={12}>
            <InputWidget
              label="Savings label"
              value={props.codSetting?.total?.save?.label}
              onChange={handleFieldChange('total.save.label')}
              hasError={!!props.errors?.total?.save?.label}
              error={props.errors?.total?.save?.label?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Font size"
              value={props.codSetting?.total?.save?.fontSize}
              onChange={handleFieldChange('total.save.fontSize')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.save?.fontSize}
              error={props.errors?.total?.save?.fontSize?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Line height"
              value={props.codSetting?.total?.save?.lineHeight}
              onChange={handleFieldChange('total.save.lineHeight')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.save?.lineHeight}
              error={props.errors?.total?.save?.lineHeight?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Letter spacing"
              value={props.codSetting?.total?.save?.letterSpacing}
              onChange={handleFieldChange('total.save.letterSpacing')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.save?.letterSpacing}
              error={props.errors?.total?.save?.letterSpacing?.message}
            />
          </Grid>
          <Grid item xs={2} container alignItems="flex-end">
            <ColorPickerWidget
              value={props.codSetting?.total?.save?.color}
              onChange={handleFieldChange('total.save.color')}
              hasError={!!props.errors?.total?.save?.color}
              error={props.errors?.total?.save?.color?.message}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Tax label',
      hasError: false,
      content: (
        <Grid container spacing={2} padding={3}>
          <Grid item xs={12}>
            <InputWidget
              label="Tax label"
              value={props.codSetting?.total?.tax?.label}
              onChange={handleFieldChange('total.tax.label')}
              hasError={!!props.errors?.total?.tax?.label}
              error={props.errors?.total?.tax?.label?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Font size"
              value={props.codSetting?.total?.tax?.fontSize}
              onChange={handleFieldChange('total.tax.fontSize')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.tax?.fontSize}
              error={props.errors?.total?.tax?.fontSize?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Line height"
              value={props.codSetting?.total?.tax?.lineHeight}
              onChange={handleFieldChange('total.tax.lineHeight')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.tax?.lineHeight}
              error={props.errors?.total?.tax?.lineHeight?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Letter spacing"
              value={props.codSetting?.total?.tax?.letterSpacing}
              onChange={handleFieldChange('total.tax.letterSpacing')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.tax?.letterSpacing}
              error={props.errors?.total?.tax?.letterSpacing?.message}
            />
          </Grid>
          <Grid item xs={2} container alignItems="flex-end">
            <ColorPickerWidget
              value={props.codSetting?.total.tax.color}
              onChange={handleFieldChange('total.tax.color')}
              hasError={!!props.errors?.total?.tax?.color}
              error={props.errors?.total?.tax?.color?.message}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Additional labels',
      hasError: false,
      content: (
        <Grid container spacing={2} padding={3}>
          <Grid item xs={12}>
            <InputWidget
              label="Promo label"
              value={props.codSetting?.body?.promoLabel}
              onChange={handleFieldChange('body.promoLabel')}
              hasError={!!props.errors?.body?.promoLabel}
              error={props.errors?.body?.promoLabel?.message}
            />
          </Grid>
          <Grid item xs={12}>
            <InputWidget
              label="Deal label"
              value={props.codSetting?.body.discount.dealUsed.label}
              onChange={handleFieldChange('body.discount.dealUsed.label')}
              hasError={!!props.errors?.body?.discount?.dealUsed?.label}
              error={props.errors?.body?.discount?.dealUsed?.label?.message}
            />
          </Grid>
          <Grid item xs={12}>
            <InputWidget
              label="Reward label"
              value={props.codSetting?.body.discount.reward.label}
              onChange={handleFieldChange('body.discount.reward.label')}
              hasError={!!props.errors?.body?.discount?.reward?.label}
              error={props.errors?.body?.discount?.reward?.label?.message}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      label: lang.overrides.displayRMHCMessage,
      hasError:
        props.showValidationErrors &&
        (!!(props.errors?.topMessage?.message as FieldError)?.message ||
          hasEmptyMessage(sanitizeRichTextInObject(props?.codSetting))),
      content: (
        <Grid container spacing={2} padding={3}>
          <Grid item xs={12}>
            <SwitchButtonWidget
              value={!props.codSetting?.topMessage?.hideRmhcMessage}
              onChange={(value) =>
                handleFieldChange('topMessage.hideRmhcMessage')(!value)
              }
            />
          </Grid>
          <InfoBox visible={props.showCodSettingRmhcMessage}>
            Setting this toggle to Hide will impact the Owner Operator function
          </InfoBox>
          {!props.showCodSettingRmhcMessage && (
            <Grid item xs={12}>
              {!!(props.errors?.topMessage?.message as FieldError)?.message && (
                <Typography color="error" variant="caption">
                  {(props.errors?.topMessage?.message as FieldError)?.message}
                </Typography>
              )}
              <RichTextEditor
                placeholder={options?.placeholder || ''}
                value={props.codSetting?.topMessage?.message}
                onChange={handleFieldChange('topMessage.message')}
                disabled={disabled}
                theme={options?.richTextTheme}
                fontFamily={mappedFonts}
                fontSize={range(8, 50).map((size) => `${size}px`)}
                letterSpacing={['-0.5px', '0px', '1px', '2px']}
                hide={hiddenToolbarItems}
              />
            </Grid>
          )}
        </Grid>
      ),
    },
  ];

  return <SidebarAccordion items={titleItems} />;
};
